import { H2, HR, Panel, Icon, Flex, Text, Box } from '@galilee/lilee';
import { UnpaddedPanelSection, UnmarginedPanel, ItemTitle, InlineText, SmallP } from "./index";
import { format } from 'date-fns';
import styled from 'styled-components';
import { MatterStatus  } from 'utils/constants';
import { OutstandingRequirements } from './OutstandingRequirementsCard'
import { useHistory } from 'react-router-dom';
import { Routes } from 'utils/constants';
import isPropValid from '@emotion/is-prop-valid';

const SizedSpan = styled.span`
    font-size: ${(p) => p.fontSize};
    color: ${(p) => p.theme.colors[p.color]};
`;

const InlineFlex = styled.div`
    display: inline-flex;
    align-items: center;
`;

const EmailLink = styled.a.withConfig({ shouldForwardProp: (prop) => isPropValid(prop) })`
    cursor: pointer;
    user-select: none;
    color: ${(p) => p.theme.colors.tertiary};
    font-size: ${(p) => p.theme.fontSizes[0]};

    * {
        font-weight: ${(p) => p.theme.fontWeights.light};
    }
`;

const StyledText = styled(Text)`
    font-weight: ${(p) => p.theme.fontWeights.light} !important;
    line-height: 1.3;
    font-size: ${(p) => p.theme.fontSizes[0]};
`;


const Header = styled(Box)`
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: auto auto;
    margin-bottom:24px;
    @media screen and (max-width: 480px) {
        grid-template-rows: 1fr 1fr;
        grid-template-columns: auto;
        grid-row-gap: 24px;
    }
`;

const ItemsContainer = styled(Box)`
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-row-gap: ${(p) => p.theme.space[6]};
    grid-column-gap: ${(p) => p.theme.space[6]};
    @media screen and (max-width: ${(p) => p.theme.breakpoints[3]}) {
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr 1fr;
    }

    @media screen and (max-width: ${(p) => p.theme.breakpoints[1]}) {
        grid-template-rows: 1fr;
        grid-template-columns: 1fr;
    }
`;

const MatterStatusContainer = styled(Flex)`
    display: none;
    @media screen and (max-width: ${(p) => p.theme.breakpointsMax[3]}) {
        display: block;
    }
`;

const StatusWrapper = styled(Flex)`
     margin-bottom: ${(p) => p.theme.space[2]};
     justify-content: flex-start;
     align-items:center;
`;

const OutStandingRequirementsContainer = styled(Box)`
    display: none;
    @media screen and (max-width: ${(p) => p.theme.breakpoints[2]}) {
        display: block;
    }
`;

const StyledLink = styled.a`
    font-size: ${(p) => p.theme.fontSizes[0]};
    color: ${(p) => p.theme.colors.link};
    margin: 3px;
`;

const MatterSummaryCard = ({ matter }) => {
    const history = useHistory();
    const currentStatus = matter.status;
    const nextStatus = MatterStatus[(MatterStatus.findIndex((i) => i === currentStatus) + 1)];

    return (
        <UnmarginedPanel>
                <Header>
                    <H2 fontSize={['18px', '18px','18px','18px', '18px','21px']}>Matter {matter.id}</H2>
                    <Flex justifyContent="space-between" alignItems="center">
                        {!!matter.settlementDescription && <StyledText fontSize={0}>Settlement Date: {matter.settlementDescription}</StyledText>}
                        <Flex alignItems="center">
                            {matter.isPexa && (
                                <Flex mr="2">
                                    <svg width={56} height={18} viewBox="0 0 150 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M0 10.3016L9.70263 24.5509L0 38.8002H9.72317L19.4052 24.5509L9.72317 10.3016H0Z" fill="#EE15C1"></path>
                                        <path
                                            d="M0 10.3016L9.70263 24.5509L0 38.8002H9.72317L19.4052 24.5509L9.72317 10.3016H0Z"
                                            fill="url(#paint0_diamond)"
                                        ></path>
                                        <path
                                            d="M22.0981 10.3014L15.0884 0V10.3014L22.0981 20.6029L29.1078 10.3014V0L22.0981 10.3014Z"
                                            fill="url(#paint1_linear)"
                                        ></path>
                                        <path
                                            d="M34.4731 10.3016L24.7705 24.5509L34.4731 38.8002H44.1963L34.4937 24.5509L44.1963 10.3016H34.4731Z"
                                            fill="url(#paint2_linear)"
                                        ></path>
                                        <path
                                            d="M150 38.7382L138.756 10.2396H132.651L121.386 38.7382H126.052C126.566 38.7382 126.977 38.6148 127.306 38.327C127.635 38.0597 127.861 37.7512 128.005 37.4428L129.958 31.9939H141.428L143.381 37.4428C143.545 37.813 143.792 38.1214 144.1 38.3681C144.429 38.6148 144.84 38.7382 145.355 38.7382H150ZM139.989 27.9022H131.417L134.521 19.2251C134.706 18.7727 134.912 18.2381 135.117 17.6007C135.323 16.9839 135.528 16.3053 135.693 15.5651C135.878 16.2848 136.083 16.9633 136.289 17.5596C136.495 18.1765 136.679 18.711 136.865 19.1634L139.989 27.9022ZM91.4763 14.8454V10.2396H73.1399V38.7382H91.4763V34.1324H79.1013V26.6479H88.7214V22.186H79.1013V14.8454H91.4763ZM71.2897 19.4307C71.2897 18.1148 71.0636 16.881 70.632 15.7502C70.1797 14.6193 69.5013 13.6529 68.5764 12.8304C67.6511 12.0079 66.4591 11.3705 65.0203 10.9181C63.5809 10.4658 61.8749 10.2396 59.9013 10.2396H50.1577V38.7382H56.1191V28.9303H59.9013C61.8132 28.9303 63.4989 28.7041 64.9378 28.2311C66.3766 27.7788 67.5484 27.1209 68.4939 26.2778C69.4395 25.4347 70.1388 24.4478 70.6113 23.2758C71.0636 22.1038 71.2897 20.8289 71.2897 19.4307ZM65.3076 19.4307C65.3076 20.9729 64.8761 22.186 64.013 23.0907C63.1493 23.9749 61.7722 24.4273 59.9013 24.4273H56.1191V14.7221H59.9013C60.8266 14.7221 61.628 14.8454 62.3064 15.051C62.9849 15.2773 63.5399 15.5857 63.9922 15.9969C64.4445 16.4081 64.7734 16.9016 64.9788 17.4979C65.2049 18.0737 65.3076 18.711 65.3076 19.4307ZM111.046 23.8104L120.214 10.2396H114.5C114.232 10.2396 113.985 10.3013 113.8 10.4246C113.615 10.548 113.43 10.7331 113.266 11.0004L107.715 19.9859C107.613 20.1504 107.51 20.3149 107.428 20.4794C107.346 20.6439 107.284 20.8083 107.201 20.9523L100.994 10.877C100.85 10.6509 100.685 10.4658 100.5 10.363C100.315 10.2602 100.027 10.1985 99.6166 10.1985H93.6553L102.741 24.0366L93.3265 38.7382H98.8764C99.2261 38.7382 99.5341 38.6354 99.7603 38.4298C99.9865 38.2242 100.192 37.998 100.377 37.7512L106.297 28.0667C106.379 27.9433 106.441 27.8199 106.503 27.676C106.565 27.5321 106.626 27.4087 106.688 27.2442L112.937 37.7307C113.081 37.998 113.286 38.2242 113.512 38.4092C113.759 38.6148 114.088 38.6971 114.479 38.6971H120.44L111.046 23.8104Z"
                                            fill="#1F2142"
                                        ></path>
                                        <path
                                            d="M22.0981 38.8822L15.0884 49.1837V38.8822L22.0981 28.5808L29.1078 38.8822V49.1837L22.0981 38.8822Z"
                                            fill="url(#paint3_linear)"
                                        ></path>
                                        <defs>
                                            <radialGradient
                                                id="paint0_diamond"
                                                cx="0"
                                                cy="0"
                                                r="1"
                                                gradientUnits="userSpaceOnUse"
                                                gradientTransform="translate(29.7115 24.8142) rotate(-180) scale(82.7885 56.6099)"
                                            >
                                                <stop stopColor="#EE15C1"></stop>
                                                <stop offset="0.751773" stopColor="#1E2142" stopOpacity="0.83"></stop>
                                                <stop offset="1" stopColor="#1E2142"></stop>
                                            </radialGradient>
                                            <linearGradient
                                                id="paint1_linear"
                                                x1="31.1538"
                                                y1="3.46244"
                                                x2="7.11328"
                                                y2="12.4414"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop offset="0.0949294" stopColor="#EE15C1"></stop>
                                                <stop offset="1" stopColor="#1E2142"></stop>
                                            </linearGradient>
                                            <linearGradient
                                                id="paint2_linear"
                                                x1="45.8417"
                                                y1="22.3948"
                                                x2="12.775"
                                                y2="28.2535"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop offset="0.0192763" stopColor="#EE15C1"></stop>
                                                <stop offset="0.230378" stopColor="#C317A7"></stop>
                                                <stop offset="0.999693" stopColor="#1E2142"></stop>
                                            </linearGradient>
                                            <linearGradient
                                                id="paint3_linear"
                                                x1="29.169"
                                                y1="50.3047"
                                                x2="2.34224"
                                                y2="28.7186"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop offset="0.0227051" stopColor="#D717B2"></stop>
                                                <stop offset="1" stopColor="#1E2142"></stop>
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </Flex>
                            )}
                            {matter.isKwil && <img src={'logo.png'} width={24} height={24} alt="kwil logo" />}
                            {matter.isEscalated && (
                                    <InlineFlex>
                                        <Icon mx={[2]} name="WarningCircle" color="warn"/>
                                        <SizedSpan fontSize="14px" color="warn">
                                            Escalated
                                        </SizedSpan>
                                    </InlineFlex>
                            )}
                        </Flex>
                    </Flex>
                    <Flex alignItems="center"> 
                        {matter.linkMatters?.length > 0 && <Icon mx={[2]} name="Link" color="base40" size="18"/>}
                        
                        {matter.linkMatters?.map((linkMatter) => (                                                
                            <StyledLink href={linkMatter} rel="noreferrer" onClick={(e) => {
                                    e.preventDefault();
                                    history.push(Routes.matter.detail.replace(':id', linkMatter));
                                }}>
                                {linkMatter}                                       
                            </StyledLink>                  
                        ))}
                    </Flex>
                </Header>
            <HR />
            <UnpaddedPanelSection borderCollapse p={[0]}>
                <Panel.Content>
                    <OutStandingRequirementsContainer my="6">
                        <OutstandingRequirements outstandingRequirements={matter.outstandingRequirements || []} />
                    </OutStandingRequirementsContainer>
                    <ItemsContainer>
                        <MatterStatusContainer>
                            <ItemTitle>Matter Status</ItemTitle>
                            <StatusWrapper>
                                <InlineText fontSize="1" color="base60" pr="4">Now:</InlineText>
                                <Icon mx={[2]} name="CheckCircle" color="tertiary" size="18"/>
                                <SizedSpan fontSize="12px">{matter.status}</SizedSpan>
                            </StatusWrapper>
                            {!!nextStatus && (
                            <StatusWrapper>
                                <InlineText fontSize="1" color="base60" pr="4">Next:</InlineText>
                                <Icon mx={[2]} name="Letter" color="base40" size="18"/>
                                <SizedSpan fontSize="12px">{nextStatus}</SizedSpan>
                            </StatusWrapper>)}
                        </MatterStatusContainer> 
                        {matter.party.originator?.name && (
                         <Box>
                            <ItemTitle>Originator</ItemTitle>
                            <SmallP>{matter.party.originator.name}</SmallP>
                        </Box>
                        )}         
                        <Box>
                            <ItemTitle>Lender (Ref No)</ItemTitle>
                            <SmallP>
                                {matter.party.lender.name} ({matter.party.lender.refNo})
                            </SmallP>
                        </Box>
                        <Box>
                            <ItemTitle>{matter.resolutionsOfficer.title}</ItemTitle> 
                            <Box>
                                <SmallP>
                                    {matter.resolutionsOfficer.name}                                                                                      
                                    <EmailLink mb={0} href={`mailto:${matter.resolutionsOfficer.email}?subject=Matter ${matter.id} Enquiry`}>                                
                                        <Icon mx={[2]} name="Email" color="base40" size="18"/>                                
                                    </EmailLink>
                                </SmallP> 
                                <SmallP>{matter.resolutionsOfficer.landline}</SmallP>
                            </Box>
                        </Box>
                        {!!matter.proposedSettlementDate && (
                            <Box>
                                <ItemTitle>Proposed Settlement</ItemTitle>
                                <SmallP>
                                    { format(new Date(matter.proposedSettlementDate), 'dd/MM/yyyy') }
                                </SmallP>
                            </Box>
                        )} 
                    </ItemsContainer>
                </Panel.Content>
            </UnpaddedPanelSection>
        </UnmarginedPanel>
    );
};

export default MatterSummaryCard;